import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { Reveal, Field, inputCls } from "@/components/ui-bits";

export default function Consultation() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ method: "online" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const interests = [1, 2, 3, 4, 5, 6, 7].map((i) => t(`s${i}.title`));
  const methods = [
    ["online", t("consult.methodOnline")],
    ["phone", t("consult.methodPhone")],
  ];

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/consultations", {
        full_name: form.full_name || "", email: form.email || "", phone: form.phone || "",
        whatsapp: form.whatsapp || "", organization: form.organization || "",
        country: form.country || "", interest: form.interest || "", method: form.method || "",
        preferred_date: form.preferred_date || "", message: form.message || "",
      });
      setDone(true);
      toast.success(t("consult.success"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="consultation-page">
      <section className="hero-grain relative overflow-hidden bg-navy-950 py-24 lg:py-28">
        <img src={IMAGES.boardroom} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/50 to-navy-950" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-gold">Stratedge UK</p>
            <h1 className="max-w-3xl font-display text-4xl font-semibold text-white sm:text-5xl">{t("consult.title")}</h1>
            <p className="mt-3 inline-block rounded-full bg-gold px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-navy" data-testid="consult-free-badge">{t("consult.free")}</p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300">{t("consult.sub")}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          {done ? (
            <Reveal className="rounded-lg border border-slate-200 bg-white p-12 text-center shadow-sm" >
              <div data-testid="consultation-success">
                <CheckCircle2 className="mx-auto h-16 w-16 text-gold" />
                <p className="mt-6 text-lg text-slate-700">{t("consult.success")}</p>
                <p className="mt-3 text-sm font-semibold text-navy">info@stratedgeuk.com</p>
              </div>
            </Reveal>
          ) : (
            <Reveal className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm sm:p-10">
              <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid="consultation-booking-form">
                <Field label={t("form.fullName")} required><input required className={inputCls} value={form.full_name || ""} onChange={set("full_name")} data-testid="consult-name" /></Field>
                <Field label={t("form.email")} required><input required type="email" className={inputCls} value={form.email || ""} onChange={set("email")} data-testid="consult-email" /></Field>
                <Field label={t("form.phone")}><input className={inputCls} value={form.phone || ""} onChange={set("phone")} data-testid="consult-phone" /></Field>
                <Field label={t("form.whatsapp")}><input className={inputCls} value={form.whatsapp || ""} onChange={set("whatsapp")} data-testid="consult-whatsapp" /></Field>
                <Field label={t("form.organization")}><input className={inputCls} value={form.organization || ""} onChange={set("organization")} data-testid="consult-org" /></Field>
                <Field label={t("form.country")}><input className={inputCls} value={form.country || ""} onChange={set("country")} data-testid="consult-country" /></Field>
                <Field label={t("consult.interest")} required>
                  <select required className={inputCls} value={form.interest || ""} onChange={set("interest")} data-testid="consult-interest">
                    <option value="">—</option>
                    {interests.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </Field>
                <Field label={t("consult.date")}><input type="date" className={inputCls} value={form.preferred_date || ""} onChange={set("preferred_date")} data-testid="consult-date" /></Field>
                <div className="sm:col-span-2">
                  <Field label={t("consult.method")}>
                    <div className="flex flex-wrap gap-3" data-testid="consult-method-group">
                      {methods.map(([value, label]) => (
                        <button
                          type="button"
                          key={value}
                          onClick={() => setForm({ ...form, method: value })}
                          data-testid={`consult-method-${value}`}
                          className={`relative rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                            form.method === value ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {label}
                          {value === "online" && (
                            <span className={`ms-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                              form.method === "online" ? "bg-gold text-navy" : "bg-gold-soft text-gold-hover"
                            }`}>{t("consult.recommended")}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label={t("form.message")}><textarea rows={4} className={inputCls} value={form.message || ""} onChange={set("message")} data-testid="consult-message" /></Field>
                </div>
                <div className="sm:col-span-2">
                  <button type="submit" disabled={sending} data-testid="consultation-submit-button"
                    className="rounded-full bg-gold px-10 py-3.5 text-sm font-bold text-navy transition-colors hover:bg-gold-hover disabled:opacity-60">
                    {sending ? t("form.sending") : t("consult.submit")}
                  </button>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
